// src/pages/Security/SecurityLayout.js

import React, { useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';
import SidebarAccordion from '../../components/SidebarAccordion';
import PageLayout from '../PageLayout';
import { SidebarContext } from '../../context/SidebarContext';
import { isSeriesActive } from '../../utils/helpers';
import './SecurityLayout.css';
import {
  FaSearch,
  FaChartLine,
  FaFileAlt,
  FaDownload,
  FaShieldAlt,
  FaUpload,
  FaHistory,
  FaArchive
} from 'react-icons/fa';

function SecurityLayout() {
  const sidebarContext = useContext(SidebarContext) || {};
  const { sidebarOpen = false } = sidebarContext;
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios
      .get('/api/exam-series')
      .then((res) => {
        setSeriesList(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam series.');
        setLoading(false);
      });
  }, []);

  // For "Browse by Series" - show only active series
  const activeSeries = seriesList.filter(series => isSeriesActive(series));
  // Past series' work doesn't disappear once a series ends - it's just not
  // part of the day-to-day OSM/RLP/CAD browsing lists above. Archives is
  // the dedicated place to reach it: one entry per expired series, each
  // expanding to the same OSM/RLP/CAD browsing options, scoped to that
  // series' id.
  const expiredSeries = seriesList.filter(series => series.expired);
  const emptySeriesChildren = [
    {
      title: loading ? 'Loading...' : 'Nothing to show here',
      path: '#',
      icon: <FaFileAlt />
    }
  ];

  const seriesChildrenFor = (examTypeSlug) =>
    activeSeries.length > 0
      ? activeSeries.map((series) => ({
          title: series.name,
          path: `/security/${examTypeSlug}/series/${series.id}`,
          icon: <FaFileAlt />
        }))
      : emptySeriesChildren;

  // Security is the only role that uploads OSM/RLP scoring guides, and it
  // also gets the same Data Team browse/download access plus the scoring
  // guide audit trail report.
  const menuItems = [
    {
      title: 'Scoring Guides',
      icon: <FaShieldAlt />,
      children: [
        {
          title: 'Upload Scoring Guides',
          path: '/security/scoring-guides',
          icon: <FaUpload />
        },
        {
          title: 'Audit Trail',
          path: '/security/reports/scoring-guide-audit',
          icon: <FaHistory />
        }
      ]
    },
    {
      title: 'OSM',
      icon: <FaFileAlt />,
      children: seriesChildrenFor('osm')
    },
    {
      title: 'RLP',
      icon: <FaFileAlt />,
      children: seriesChildrenFor('rlp')
    },
    {
      title: 'CAD',
      icon: <FaFileAlt />,
      children: [
        {
          title: 'Browse per Candidate',
          path: '/security/cad/candidates',
          icon: <FaSearch />
        },
        ...(activeSeries.length > 0
          ? activeSeries.map((series) => ({
              title: `Browse per Centre - ${series.name}`,
              path: `/security/cad/centres/${series.id}`,
              icon: <FaDownload />
            }))
          : emptySeriesChildren)
      ]
    },
    {
      title: 'Reports',
      icon: <FaChartLine />,
      children: [
        {
          title: 'Deployment List',
          path: '/security/reports/deployment-list',
          icon: <FaFileAlt />
        }
      ]
    },
    {
      // One folder per expired series; each expands to OSM/RLP/CAD, and
      // CAD itself expands further to Browse per Candidate (search/download
      // an individual candidate's folder) and Browse by Centre (download
      // the bundled folders for a whole exam centre) - the same two CAD
      // access patterns available for active series, just scoped to a
      // specific archived series.
      title: 'Archives',
      icon: <FaArchive />,
      children: expiredSeries.length > 0
        ? expiredSeries.map((series) => ({
            title: series.name,
            children: [
              { title: 'OSM', path: `/security/osm/series/${series.id}`, icon: <FaFileAlt /> },
              { title: 'RLP', path: `/security/rlp/series/${series.id}`, icon: <FaFileAlt /> },
              {
                title: 'CAD',
                children: [
                  { title: 'Browse per Candidate', path: '/security/cad/candidates', icon: <FaSearch /> },
                  { title: 'Browse by Centre', path: `/security/cad/centres/${series.id}`, icon: <FaDownload /> }
                ]
              }
            ]
          }))
        : emptySeriesChildren
    }
  ];

  return (
    <div className="security-layout">
      <div className={`security-layout-body ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {error && <div className="text-danger px-3">{error}</div>}
        <SidebarAccordion menuItems={menuItems} treeStyle />
        <div className="main-content">
          <PageLayout>
            <Outlet />
          </PageLayout>
        </div>
      </div>
    </div>
  );
}

export default SecurityLayout;
